import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, ELEVATION, TYPOGRAPHY } from '../../theme';
import { useUIStore, useMapPathsOverlayActive } from '../../store/uiStore';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { useTabBarClearance } from '../../hooks/useTabBarClearance';
import { useAuthStore } from '../../store/authStore';
import { useOverlayFade } from '../../hooks/useAnimations';

const AUTO_DISMISS_MS = 10_000;

/**
 * One-time floating callout that points down at the floating tab bar's "+"
 * button. Replaces the old green "Welcome, Fire Scout" banner: instead of
 * a wall of text in the search area calling everyone a Fire Scout, this is
 * a single line aimed at the action the user actually needs to discover.
 *
 * Self-gated: persists dismissal in AsyncStorage, hides when the structure
 * detail sheet is open (tab bar hidden), hides for users who already have
 * posts, and hides while MapPathsOverlay is up for any reason — first visit
 * or a Guidance-tab reopen — so the two prompts never stack (the overlay
 * always wins). The coachmark doesn't render anything until its visibility
 * has been resolved to avoid a flash on subsequent launches.
 */
export function CreatePostCoachmark() {
  const { colors } = useTheme();
  const tabBarVisible = useUIStore((s) => s.tabBarVisible);
  const hasSeenCreateCoachmark = useUIStore((s) => s.hasSeenCreateCoachmark);
  // Shared selector — same boolean MapSignInOverlay suppresses off of, and
  // that MapPathsOverlay itself uses to decide whether it's on screen.
  const pathsOverlayActive = useMapPathsOverlayActive();
  const hydrateCreateCoachmark = useUIStore((s) => s.hydrateCreateCoachmark);
  const markCreateCoachmarkSeen = useUIStore((s) => s.markCreateCoachmarkSeen);
  // Desktop shell has no floating "+" — creation lives in the sidebar's
  // New post button, so the callout would point at nothing.
  const isDesktopShell = useDesktopShell();
  const tabBarClearance = useTabBarClearance();
  const user = useAuthStore((s) => s.user);
  const [visible, setVisible] = useState(false);
  const dismissedRef = useRef(false);

  // Computed above the fade, not only as the early returns below: each of
  // these unmounts the Animated.View, and an unmount stops a running fade
  // where it stands (see useOverlayFade). Without it in the fade's input the
  // callout can come back invisible and still take clicks, over the bottom
  // of the map at zIndex 999.
  //
  // Re-read every render, so an anonymous user who just made their first post
  // sees the callout go without needing a reload, and MapPathsOverlay keeps
  // the screen to itself while it's up (first visit or a Guidance reopen).
  const gated =
    (user?.postsCount ?? 0) > 0 || !tabBarVisible || isDesktopShell || pathsOverlayActive;
  const shown = visible && !gated;
  // box-none, not auto: the wrapper spans the full width and only the
  // centered callout should take clicks.
  const { opacity, pointerEvents, fadeOut } = useOverlayFade(shown, { whenShown: 'box-none' });

  // Hydrate the persisted flag on mount.
  useEffect(() => {
    void hydrateCreateCoachmark();
  }, [hydrateCreateCoachmark]);

  // Show when hydration resolves to "not seen".
  useEffect(() => {
    if (hasSeenCreateCoachmark === false) setVisible(true);
  }, [hasSeenCreateCoachmark]);

  const dismiss = useCallback(() => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    fadeOut(() => {
      setVisible(false);
      markCreateCoachmarkSeen();
    });
  }, [fadeOut, markCreateCoachmarkSeen]);

  // Auto-dismiss after a generous read window. Keyed off `shown`, so the
  // clock restarts when a gate reopens — a callout nobody could see
  // shouldn't have been spending its read window.
  useEffect(() => {
    if (!shown) return;
    const timer = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [shown, dismiss]);

  if (!visible) return null;
  if (gated) return null;

  return (
    <Animated.View
      testID="create-post-coachmark"
      pointerEvents={pointerEvents}
      style={[styles.wrapper, { bottom: tabBarClearance, opacity }]}>
      <TouchableOpacity
        onPress={dismiss}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel="Dismiss tip"
        style={[
          styles.callout,
          {
            backgroundColor: colors.surfaceElevated,
            borderColor: colors.border,
          },
        ]}>
        <Text style={[styles.text, { color: colors.text }]} numberOfLines={1}>
          See something? Tap + to share.
        </Text>
      </TouchableOpacity>
      <View
        style={[
          styles.pointer,
          {
            borderTopColor: colors.surfaceElevated,
          },
        ]}
      />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 999,
  },
  callout: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
    ...ELEVATION.floating,
  },
  text: {
    ...TYPOGRAPHY.label,
  },
  pointer: {
    width: 0,
    height: 0,
    marginTop: -1,
    borderLeftWidth: 7,
    borderRightWidth: 7,
    borderTopWidth: 8,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
  },
});
